/**
 * Tasks Service — Cliente API completo del módulo de tareas.
 *
 * Endpoints: /api/ClinicalOperations/tasks*
 * Backend SP: ops.SP_*_TASK*
 * Documentación: docs/modulos/04_TASKS_ENDPOINTS_GUIDE.md
 *
 * Todas las funciones convierten los `keys` (categoryKey, priorityKey, statusKey)
 * usados en el frontend a los IDs numéricos que espera el backend.
 */

import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import {
  categoryIdFromKey,
  priorityIdFromKey,
  statusIdFromKey,
} from "../constants/taskConstants";

const API_URL = `${API_BASE_URL}/api/ClinicalOperations`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * Normaliza una tarea recibida del backend para uso en componentes.
 * El backend ya devuelve `categoryKey`, `priorityKey`, `statusKey` denormalizados.
 * Aquí solo aplanamos `dueDate` (YYYY-MM-DD) y exponemos `id`/`category`/`priority`/`status`
 * como aliases para los componentes existentes que esperan esas keys.
 */
export const normalizeTask = (t) => ({
  ...t,
  id: t.taskId,
  category: t.categoryKey,
  priority: t.priorityKey,
  status: t.statusKey,
  dueDate: t.dueDate ? String(t.dueDate).split("T")[0] : null,
  createdBy: t.creatorName,
});

// ── 1. Crear tarea ──────────────────────────────────────────────────

/**
 * POST /api/ClinicalOperations/tasks
 *
 * @param {Object} taskData
 * @param {string} taskData.title - Requerido
 * @param {string} [taskData.description]
 * @param {number|string} taskData.categoryId|category - ID o key
 * @param {number|string} taskData.priorityId|priority - ID o key
 * @param {string} [taskData.dueDate] - YYYY-MM-DD
 * @param {string} [taskData.cipId]
 * @param {string} [taskData.patientName]
 * @param {number} [taskData.locationId]
 * @param {string} [taskData.relatedType]
 * @param {number} [taskData.assigneeId]
 * @param {number} taskData.creatorId - Requerido
 *
 * Respuesta: { result: 0, message: "Task created successfully", taskId: 88 }
 * Error 400: { result: 1, message: "Invalid task category" }
 */
export const createTask = async (taskData) => {
  const categoryId =
    taskData.categoryId ?? categoryIdFromKey(taskData.category);
  const priorityId =
    taskData.priorityId ?? priorityIdFromKey(taskData.priority);

  const payload = {
    title: taskData.title || "",
    description: taskData.description || null,
    categoryId,
    priorityId,
    dueDate: taskData.dueDate || null,
    cipId: taskData.cipId ? String(taskData.cipId) : null,
    patientName: taskData.patientName || null,
    locationId: taskData.locationId || null,
    relatedType: taskData.relatedType || null,
    assigneeId: taskData.assigneeId || null,
    creatorId: taskData.creatorId,
  };
  const { data } = await axios.post(`${API_URL}/tasks`, payload, authHeaders());
  return data;
};

// ── 2. Listar tareas (vista admin/manager) ──────────────────────────

/**
 * GET /api/ClinicalOperations/tasks
 *
 * @param {Object} [filters]
 * @param {number} [filters.locationId]
 * @param {number|string} [filters.status] - ID o key
 * @param {number|string} [filters.priorityId]
 * @param {number|string} [filters.categoryId]
 * @param {number} [filters.assigneeId]
 * @param {number} [filters.creatorId]
 * @param {string} [filters.cipId]
 * @param {string} [filters.relatedType]
 *
 * Respuesta: array de tareas con campos denormalizados.
 */
export const getTasks = async (filters = {}) => {
  const params = {};
  if (filters.locationId)  params.locationId  = filters.locationId;
  if (filters.status)      params.status      = typeof filters.status === "string"     ? statusIdFromKey(filters.status)     : filters.status;
  if (filters.priorityId)  params.priorityId  = typeof filters.priorityId === "string" ? priorityIdFromKey(filters.priorityId) : filters.priorityId;
  if (filters.categoryId)  params.categoryId  = typeof filters.categoryId === "string" ? categoryIdFromKey(filters.categoryId) : filters.categoryId;
  if (filters.assigneeId)  params.assigneeId  = filters.assigneeId;
  if (filters.creatorId)   params.creatorId   = filters.creatorId;
  if (filters.cipId)       params.cipId       = String(filters.cipId);
  if (filters.relatedType) params.relatedType = filters.relatedType;

  const { data } = await axios.get(`${API_URL}/tasks`, { ...authHeaders(), params });
  return Array.isArray(data) ? data.map(normalizeTask) : [];
};

// ── 3. Mis tareas (creadas + asignadas + auto-asignadas) ────────────

/**
 * GET /api/ClinicalOperations/tasks/my-tasks?employeeId=X
 *
 * Retorna las tareas relacionadas con un empleado en una sola consulta,
 * con un campo extra `myRole` ∈ { "creator", "assignee", "both" }.
 *
 * @param {number} employeeId - Requerido
 * @param {Object} [filters] - Mismos filtros que getTasks (excepto assigneeId/creatorId)
 *
 * Respuesta: array de tareas con `myRole`.
 * Error 400: { message: "EmployeeId is required." }
 */
export const getMyTasks = async (employeeId, filters = {}) => {
  if (!employeeId) {
    throw new Error("employeeId is required for getMyTasks");
  }
  const params = { employeeId };
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.status)     params.status     = typeof filters.status === "string"     ? statusIdFromKey(filters.status)     : filters.status;
  if (filters.priorityId) params.priorityId = typeof filters.priorityId === "string" ? priorityIdFromKey(filters.priorityId) : filters.priorityId;
  if (filters.categoryId) params.categoryId = typeof filters.categoryId === "string" ? categoryIdFromKey(filters.categoryId) : filters.categoryId;

  const { data } = await axios.get(`${API_URL}/tasks/my-tasks`, { ...authHeaders(), params });
  return Array.isArray(data) ? data.map(normalizeTask) : [];
};

// ── 4. Detalle de una tarea ─────────────────────────────────────────

/**
 * GET /api/ClinicalOperations/tasks/{id}
 *
 * Retorna la tarea con todos los campos + `locationName` + `lastUpdatedBy`.
 * Error 404: { result: 1, message: "Task not found" }
 */
export const getTaskById = async (taskId) => {
  const { data } = await axios.get(`${API_URL}/tasks/${taskId}`, authHeaders());
  return data ? normalizeTask(data) : null;
};

// ── 5. Estadísticas ─────────────────────────────────────────────────

/**
 * GET /api/ClinicalOperations/tasks/stats
 *
 * Respuesta: { totalTasks, totalPending, totalInProgress, totalCompleted,
 *              totalCancelled, totalHigh, totalMedium, totalLow,
 *              totalOverdue, totalActive }
 */
export const getTaskStats = async (filters = {}) => {
  const params = {};
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.assigneeId) params.assigneeId = filters.assigneeId;
  if (filters.creatorId)  params.creatorId  = filters.creatorId;

  const { data } = await axios.get(`${API_URL}/tasks/stats`, { ...authHeaders(), params });
  return data || {};
};

// ── 6. Editar tarea ─────────────────────────────────────────────────

/**
 * PUT /api/ClinicalOperations/tasks/{id}
 *
 * Solo el creador o el assignee pueden editar. No edita tareas completed/cancelled.
 * Patrón COALESCE — solo se actualizan los campos enviados (no-null).
 *
 * @param {number} taskId
 * @param {Object} taskData - cualquier subset de campos editables
 * @param {number} taskData.employeeId - Requerido (del JWT)
 *
 * Respuesta: { result: 0, message: "Task updated successfully" }
 * Errores: 400 con mensajes específicos (Task not found, Cannot edit completed,
 *          Only creator/assignee, Invalid category/priority, Assignee not found)
 */
export const updateTask = async (taskId, taskData) => {
  if (!taskData.employeeId) {
    throw new Error("employeeId is required for updateTask");
  }
  const categoryId =
    taskData.categoryId ?? (taskData.category ? categoryIdFromKey(taskData.category) : null);
  const priorityId =
    taskData.priorityId ?? (taskData.priority ? priorityIdFromKey(taskData.priority) : null);

  const payload = {
    employeeId: taskData.employeeId,
    title: taskData.title ?? null,
    description: taskData.description ?? null,
    categoryId,
    priorityId,
    dueDate: taskData.dueDate ?? null,
    cipId: taskData.cipId ? String(taskData.cipId) : null,
    patientName: taskData.patientName ?? null,
    locationId: taskData.locationId ?? null,
    relatedType: taskData.relatedType ?? null,
    assigneeId: taskData.assigneeId ?? null,
  };
  const { data } = await axios.put(`${API_URL}/tasks/${taskId}`, payload, authHeaders());
  return data;
};

// ── 7. Cambiar estado ───────────────────────────────────────────────

/**
 * PATCH /api/ClinicalOperations/tasks/{id}/status
 *
 * @param {number} taskId
 * @param {number|string} newStatus - ID o key (26-29 / pending|in_progress|completed|cancelled)
 * @param {number} [employeeId]
 */
export const updateTaskStatus = async (taskId, newStatus, employeeId = null) => {
  const statusId = typeof newStatus === "string" ? statusIdFromKey(newStatus) : newStatus;
  const { data } = await axios.patch(
    `${API_URL}/tasks/${taskId}/status`,
    { newStatus: statusId, employeeId },
    authHeaders()
  );
  return data;
};

// ── 8. Marcar completada (shortcut) ─────────────────────────────────

/**
 * PATCH /api/ClinicalOperations/tasks/{id}/complete
 *
 * Equivalente a updateTaskStatus(id, 28).
 */
export const completeTask = async (taskId, employeeId = null) => {
  const { data } = await axios.patch(
    `${API_URL}/tasks/${taskId}/complete`,
    { employeeId },
    authHeaders()
  );
  return data;
};

// ── 9. Eliminar tarea (hard delete) ─────────────────────────────────

/**
 * DELETE /api/ClinicalOperations/tasks/{id}
 *
 * Solo el creador puede eliminar.
 * Error 400: { result: 1, message: "Only the task creator can delete this task" }
 */
export const deleteTask = async (taskId, employeeId) => {
  if (!employeeId) {
    throw new Error("employeeId is required for deleteTask");
  }
  const { data } = await axios.delete(`${API_URL}/tasks/${taskId}`, {
    ...authHeaders(),
    data: { employeeId },
  });
  return data;
};
